import { Divider } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import Cookies from "js-cookie";
import { isEmpty } from "lodash";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";
import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getItem } from "../../../helpers/data_management";
import { debounce } from "../../../helpers/debounce";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import useVisitorForceSignup from "../../../hooks/useVisitorForceSignup";
import {
  updateBulkApplyState,
  updateCurrentSalaryDialog,
  updatePostApplyDialog,
  updateSGJobReminderDialog,
  updateShowSimilarJobsStatus,
} from "../../../redux/actions/dialog_action";
import {
  getJobTypes,
  getSpecialisation,
  getStateRegions,
  updateJobListFilter,
  updateSelectedJob,
} from "../../../redux/actions/job_action";
import {
  checkAuthentication,
  getUser,
} from "../../../redux/actions/user_action";
import { requiresConnorsApplyConfirm } from "../../shared/ConnorsApplyConfirm/connorsApplyCompany";
import { getJobCompanyName } from "../../shared/ConnorsApplyConfirm/connorsApplyUtils";
import SharedToast from "../../shared/SharedToast/SharedToast";
import { HeightSpacer } from "../../shared/Spacer/styles";
import JobAlertSticky from "../JobAlertSticky/JobAlertSticky";
import SGJobPageReminderDialog, {
  SHOW_SG_JOB_REMINDER_DIALOG_KEY,
} from "../SGJobPageReminderDialog/SGJobPageReminderDialog";
import SingleJobAiSummary from "./SingleJobAiSummary/SingleJobAiSummary";
import SingleJobApplyButtonGroup from "./SingleJobApplyButtonGroup/SingleJobApplyButtonGroup";
import SingleJobCompanyProfile from "./SingleJobCompanyProfile/SingleJobCompanyProfile";
import SingleJobDescription from "./SingleJobDescription/SingleJobDescription";
import SingleJobHighlight from "./SingleJobHighlight/SingleJobHighlight";
import SingleJobHighlightWithLogo from "./SingleJobHighlight/SingleJobHighlightWithLogo";
import SingleJobMedia from "./SingleJobMedia/SingleJobMedia";
import SingleJobSimilarJobs from "./SingleJobSimilarJobs/SingleJobSimilarJobs";
import SingleJobTitleSection from "./SingleJobTitleSection/SingleJobTitleSection";
import {
  BoxStyled,
  DivStyled,
  JobAlertContainer,
  JobPageContainer,
  JobSparkleIcon,
  SimilarJobsButton,
} from "./styles";

// New Popup For incomplete resume
const CreateResumePopup = dynamic(() =>
  import(
    "../../profile/ResumePopups/ApplyJobResumeStatus/CreateResume/CreateResume"
  )
);

const VisitorJobAlertDialog = dynamic(() =>
  import("../VisitorJobAlertDialog/VisitorJobAlertDialog")
);

const JobAlertsCreateDialog = dynamic(() =>
  import("../../profile/JobAlertsCreateDialog/JobAlertsCreateDialog")
);

const VisitorJobAlertSuccesDialog = dynamic(() =>
  import(
    "../VisitorJobAlertDialog/VisitorJobAlertSuccessDialog/VisitorJobAlertSuccessDialog"
  )
);

const RejectedResumePopup = dynamic(() =>
  import(
    "../../profile/ResumePopups/ApplyJobResumeStatus/RejectedResume/RejectedResume"
  )
);

const BaseDialog = dynamic(() =>
  import("../SecondaryVisitor/BaseDialog/BaseDialog")
);

const RefreshDialog = dynamic(() =>
  import("../../for-you/RefreshDialog/RefreshDialog")
);

const PostApplyDialog = dynamic(() =>
  import("../PostApplyDialog/PostApplyDialog")
);
// End new popup for incomplete resume

const JobDetailsJobAlert = dynamic(() =>
  import("../JobDetailsJobAlert/JobDetailsJobAlert")
);

function NewSingleJobWrapper(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const stateRegionsList = useSelector((state) => state?.jobs?.stateRegions);
  const specializationList = useSelector(
    (state) => state?.jobs?.specialisation
  );
  const jobTypesList = useSelector((state) => state?.jobs?.jobTypes);

  const isLoggedIn = checkAuthentication();
  const [dialogOpen, setDialogOpen] = useState(true);

  const router = useRouter();
  const query = router.query;
  const { trackJdpViewGate } = useVisitorForceSignup();
  const jobOrigin = router.query.origin ?? null;

  const belowLg = useMediaQuery("(max-width: 1279px)");
  const belowTablet = useMediaQuery("(max-width: 767px)");

  const user = getUser();

  const [showStickyJobAlert, setShowStickyJobAlert] = useState(false);
  const [checkSimilarJob, setCheckSimilarJob] = useState([]);
  const [fromJobAlertEmail, setFromJobAlertEmail] = useState(false);

  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = useRef(null);
  const jobListRef = useRef(null);
  const toastQueue = useRef([]);

  useEffect(() => {
    const handleScroll = () => {
      if (triggerRef?.current) {
        const rect = triggerRef?.current?.getBoundingClientRect();
        setIsVisible(rect?.top <= 108); //navbar + banner
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Query URL utm for job alert email
  useEffect(() => {
    const utmCampaign = query.utm_campaign;
    const utmMedium = query.utm_medium;

    if (utmCampaign === "job_alert" && utmMedium === "email") {
      setFromJobAlertEmail(true);
      sessionStorage.setItem("from_job_alert_email", "true");
    } else {
      setFromJobAlertEmail(false);
      sessionStorage.removeItem("from_job_alert_email");
    }
  }, [query.utm_campaign, query.utm_medium]);

  useEffect(() => {
    if (localStorage.getItem("showCurrentSalaryDialogAfterJump") === "1") {
      const dontShowKey = `currentSalaryDialogDontShow_${user?.id}`;
      const dontShow = localStorage.getItem(dontShowKey);

      if (user && !user.currentSalary && !dontShow) {
        dispatch(updateCurrentSalaryDialog(true));
        localStorage.removeItem("showCurrentSalaryDialogAfterJump");
      } else {
        localStorage.removeItem("showCurrentSalaryDialogAfterJump");
      }
    }
  });

  function updateSGJobPageReminderDialogState() {
    if (query["source"] === "my") {
      // Read user preference of displaying reminder dialog from LocalStorage
      const shouldDisplayBasedOnPreference = getItem(
        SHOW_SG_JOB_REMINDER_DIALOG_KEY
      );
      // NOTE: Value return from LocalStorage is String, so check for String "true"
      // If the key not found, then display also
      if (
        shouldDisplayBasedOnPreference === "true" ||
        !shouldDisplayBasedOnPreference
      ) {
        dispatch(updateSGJobReminderDialog(true));
      } else {
        dispatch(updateSGJobReminderDialog(false));
      }
    } else {
      dispatch(updateSGJobReminderDialog(false));
    }
  }

  useEffect(() => {
    dispatch(getSpecialisation());
    dispatch(getStateRegions());
    dispatch(getJobTypes());
  }, [dispatch]);

  useEffect(() => {
    const cleanSalary = props.serverJob?.salary
      ?.split("-")
      .map((item) => item.replaceAll("RM", ""));

    // posthog.capture("job_view", {
    //   job_id: props.serverJob?.id,
    //   job_title: props.serverJob?.title,
    //   job_type: props.serverJob?.jobType,
    //   job_discovery: jobOrigin,
    //   spotlight: props.serverJob?.spotlight,
    //   featured: props.serverJob?.boosted,
    //   keyword: props.serverJob?.keywordHighlight,
    //   scraped:
    //     props.serverJob?.category === "aggregated" ||
    //     props.serverJob?.category === "scraped"
    //       ? true
    //       : false,
    //   category: props.serverJob?.category ?? "-",
    //   job_slot_type: props.serverJob?.jobSlotType,
    //   track_titles: props.serverJob?.tracks.map((track) => track?.title) ?? [],
    //   company_id: props.serverJob?.company?.id,
    //   company_name: props.serverJob?.company?.name,
    //   location: props.serverJob?.location,
    //   state_region: props.serverJob?.stateRegion,
    //   hide_salary:
    //     !props.serverJob?.salary ||
    //     props.serverJob?.salary.toString().toLowerCase().includes("undisclosed")
    //       ? true
    //       : false,
    //   career_level: props.serverJob?.careerLevel,
    //   min_years_experience: props.serverJob?.minYearsExperience,
    //   max_years_experience: props.serverJob?.maxYearsExperience,
    //   min_salary_range: parseInt(cleanSalary[0]) ?? 0,
    //   max_salary_range: parseInt(cleanSalary[cleanSalary.length - 1]) ?? 0,
    // });

    sendTrackingEvent({
      event: "CE_job-view-ijp",
      "job-id": props.serverJob?.id ?? null,
      "job-tracks": JSON.stringify(props.serverJob?.tracks) ?? null,
      "job-title": props.serverJob?.title ?? null,
      "job-company": props.serverJob?.company?.name ?? null,
      "company-id": props.serverJob?.company?.id ?? null,
    });

    setTimeout(() => {
      Cookies.set("click-source", null);
    }, 30000);
    setShowStickyJobAlert(true);
  }, []);

  useEffect(() => {
    updateSGJobPageReminderDialogState();
  }, [query]);

  useEffect(() => {
    dispatch(updateSelectedJob(props.serverJob));
  }, [props.serverJob]);

  useEffect(() => {
    if (!props.serverJob?.id) return;

    trackJdpViewGate({
      jobId: props.serverJob.id,
      origin: router.query.origin ?? null,
    });
  }, [props.serverJob?.id, router.query.origin, trackJdpViewGate]);

  function triggerToast(toastMessage, state) {
    toast(toastMessage, { className: state ? "Toast-success" : "Toast-error" });
  }

  function locationFilterableLink(job) {
    const jobStateSlug = handleHref("stateRegion", job);

    if (job?.location == job?.stateRegion) {
      return <Link href={jobStateSlug}>{job?.location}</Link>;
    }

    return (
      <>
        {job?.location}, <Link href={jobStateSlug}>{job?.stateRegion}</Link>
      </>
    );
  }

  function onSimilarJobsClick(e, job) {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    const companyName = getJobCompanyName(job) || job?.aggregatedCompanyName;

    if (requiresConnorsApplyConfirm(companyName)) {
      window.open("/connors-collective", "_blank");
      return;
    }

    sendTrackingEvent({
      event: "CE_view_similar_jobs_click",
      origin: "job_detail_page",
      job_discovery: "view_similar_jobs",
      is_visitor: user === null ? "true" : "false",
    });

    dispatch(updateShowSimilarJobsStatus(true));
    dispatch(updateBulkApplyState("post-apply"));

    dispatch(
      updatePostApplyDialog({
        status: true,
        scraped: job?.category === "scraped",
        jobGlobalId: job?.globalId,
        jobId: job?.id,
        internship: job?.jobType === "Internship",
      })
    );
  }

  function handleHref(type, job) {
    //to prevent the browser spamming dispatch when user return to previous page
    //to clear the title, if not the search bar is not empty
    const debouncedDispatch = debounce((dispatch) => {
      if (type !== "title") dispatch(updateJobListFilter({ keyword: "" }));
    }, 500);

    debouncedDispatch(dispatch);

    switch (type) {
      case "jobTitle":
        const jobTitle = job?.title ?? job?.aggregatedCompanyName;
        const processedJobTitle = jobTitle
          .replaceAll(/[(){}[\]"`~,./&%$*!@-]/g, "")
          .trim()
          .replaceAll(" ", "-");

        return `/${encodeURIComponent(
          processedJobTitle
        )}-jobs?filterOrigin=jdp-text`;

      case "stateRegion":
        const jobState = stateRegionsList.filter(
          (state) => state?.state == job?.stateRegion
        );
        const jobStateSlug = !isEmpty(jobState)
          ? jobState[0]?.state?.replace(" ", "+")
          : "Overseas";

        return `/jobs?state=${jobStateSlug}&filterOrigin=jdp-text`;

      case "jobType":
        const jobType = job?.jobType
          ? job?.jobType
          : job?.job_type
          ? job?.job_type
          : null;

        const selectedJobType = jobTypesList?.filter(
          (currentJobType) =>
            currentJobType?.title?.toLowerCase() == jobType?.toLowerCase()
        );

        return `/jobs?job-type=${selectedJobType[0]?.id}&filterOrigin=jdp-text`;

      case "specialisation":
        let specialisationSlug = "";
        const jobTracks = job?.tracks;

        jobTracks?.forEach((track) => {
          specialisationSlug += "specialisation=" + track?.id + "&";
        });

        return `/jobs?${specialisationSlug.slice(
          0,
          specialisationSlug.length - 1
        )}&filterOrigin=jdp-text`;
      case "experience":
        let experienceURL = "/jobs?";
        const yearsExperience = {
          min: job?.minYearsExperience,
          max: job?.maxYearsExperience > 11 ? 11 : job?.maxYearsExperience,
        };
        const experienceList = [
          [-1],
          [0],
          [1, 2, 3],
          [4, 5, 6, 7],
          [8, 9, 10],
          [11],
        ];

        const minRange = experienceList.find((value) =>
          value.includes(yearsExperience?.min)
        );
        const indexInExpListMin = experienceList.indexOf(minRange) + 1;

        const maxRange = experienceList.find((value) =>
          value.includes(yearsExperience?.max)
        );
        const indexInExpListMax = experienceList.indexOf(maxRange) + 1;

        if (
          indexInExpListMin == indexInExpListMax &&
          job?.jobType != "Internship"
        ) {
          experienceURL += `experience=${indexInExpListMin}&`;
        } else if (
          indexInExpListMin == indexInExpListMax &&
          job?.jobType == "Internship"
        ) {
          experienceURL += `experience=${1}&`;
        } else {
          for (let i = 0; i < experienceList.length; i++) {
            if (i + 1 >= indexInExpListMin && i + 1 <= indexInExpListMax) {
              experienceURL += `experience=${i + 1}&`;
            }
          }
        }
        return `${experienceURL.slice(
          0,
          experienceURL.length - 1
        )}&filterOrigin=jdp-text`;
      default:
        return;
    }
  }

  return (
    <Fragment>
      <SharedToast />
      <SGJobPageReminderDialog />
      <JobPageContainer>
        <HeightSpacer $height={"30px"} />
        <section aria-label="job-title-section">
          <SingleJobTitleSection
            job={props.serverJob}
            handleHref={handleHref}
          />
        </section>

        {/* this is the ai summary section with job highlight */}
        {/* Only show if gptSummary exists (applies to all job categories including aggregated) */}
        {/* {!props.serverJob.gptSummary ? ( */}
        <section aria-label="job-summary-section">
          <BoxStyled
            flexDirection={belowTablet ? "column-reverse" : null}
            gap={belowTablet ? "1rem" : "0.5rem"}
            margin={"0 0 2rem"}
            height={
              belowLg ? (belowTablet ? "100%" : "fit-content") : "fit-content"
            }
            minHeight={
              belowLg ? (belowTablet ? "100%" : "15.25rem") : "13.875rem"
            }
          >
            <SingleJobAiSummary job={props.serverJob} />
            <BoxStyled
              flexDirection={"column"}
              gap={belowTablet ? "1rem" : "0.5rem"}
              maxWidth={
                belowLg ? (belowTablet ? "100%" : "16.1875rem") : "25.125rem"
              }
              minWidth={
                belowLg ? (belowTablet ? "100%" : "16.1875rem") : "25.125rem"
              }
              width={"100%"}
            >
              <SingleJobHighlight
                job={props.serverJob}
                locationFilter={locationFilterableLink}
                handleHref={handleHref}
              />
              {belowTablet && <JobDetailsJobAlert job={props.serverJob} />}
              <SimilarJobsButton
                onClick={(e) => onSimilarJobsClick(e, props.serverJob)}
              >
                <JobSparkleIcon />
                {t("jobs.single_job.view_similar_jobs")}
              </SimilarJobsButton>
            </BoxStyled>
          </BoxStyled>
        </section>
        {/* ) : ( */}
        {/* Fallback for jobs without AI summary (including aggregated jobs on tablet) */}
        {/* belowTablet && (
            <section aria-label="job-summary-section">
              <DivStyled
                container
                spacing={2}
                columns={{ xs: 4, tablet: 12 }}
                flexDirection={belowTablet ? "column-reverse" : null}
                marginBottom={"20px"}
              >
                <DivStyled item xs={4} tablet={4}>
                  <SingleJobHighlight job={props.serverJob} />
                  <JobDetailsJobAlert job={props.serverJob} />
                </DivStyled>
              </DivStyled>
            </section>
          )
        )} */}

        {!props.serverJob?.company?.recruitmentFirm && (
          <section aria-label="job-media-section">
            <SingleJobMedia job={props.serverJob} />
          </section>
        )}

        <section
          aria-label="job-description-body"
          style={{ paddingTop: "25px" }}
        >
          <DivStyled
            ref={triggerRef}
            container
            spacing={2}
            columns={{ xs: 4, lg: 13 }}
            flexDirection={belowLg ? null : "row"}
            display={"flex"}
            justifyContent={"space-between"}
            gap={"2rem"}
          >
            <DivStyled item xs={4} lg={8}>
              <section aria-label="job-description-details-section">
                <SingleJobDescription
                  job={props.serverJob}
                  jobDetails={props.jobDetails}
                  handleHref={handleHref}
                />
              </section>
              <Divider style={{ margin: "30px 0px" }} />
              {!props.serverJob.category !== "aggregated" ? (
                <section aria-label="company-profile-short-intro">
                  <div
                    style={{
                      marginBottom: checkSimilarJob?.length > 0 ? 0 : "50px",
                    }}
                  >
                    <SingleJobCompanyProfile job={props.serverJob} />
                  </div>
                  {checkSimilarJob?.length > 0 && (
                    <Divider style={{ margin: "30px 0px" }} />
                  )}
                </section>
              ) : null}
              <section aria-label="similar-job-section">
                <SingleJobSimilarJobs
                  setCheckSimilarJob={setCheckSimilarJob}
                  job={props.serverJob}
                />
              </section>
            </DivStyled>
            {!belowLg && isVisible ? (
              <BoxStyled
                flexDirection={"column"}
                maxWidth={"25.125rem"}
                width={"100%"}
                height={"fit-content"}
                position={"sticky"}
                top={"130px"}
                marginBottom={"50px"}
                rowGap={"0.875rem"}
              >
                <SingleJobHighlightWithLogo
                  job={props.serverJob}
                  locationFilter={locationFilterableLink}
                  handleHref={handleHref}
                />
                <JobDetailsJobAlert job={props.serverJob} />
                <SimilarJobsButton
                  onClick={(e) => onSimilarJobsClick(e, props.serverJob)}
                >
                  <JobSparkleIcon />
                  {t("jobs.single_job.view_similar_jobs")}
                </SimilarJobsButton>
              </BoxStyled>
            ) : null}
          </DivStyled>
        </section>
        <CreateResumePopup />
        <RejectedResumePopup />
        <VisitorJobAlertDialog />
        <VisitorJobAlertSuccesDialog />
        <JobAlertsCreateDialog
          isLoggedIn={isLoggedIn}
          triggerToast={triggerToast}
        />
        <JobAlertsCreateDialog
          isLoggedIn={isLoggedIn}
          triggerToast={triggerToast}
        />
        <BaseDialog />
        <PostApplyDialog
          jobListRef={jobListRef}
          jobDiscovery={jobOrigin}
          setDialogOpen={setDialogOpen}
        />
      </JobPageContainer>
      {showStickyJobAlert && user === null && (
        <JobAlertContainer>
          <JobAlertSticky job={props.serverJob} />
        </JobAlertContainer>
      )}
      {belowLg ? (
        <DivStyled
          sx={{
            position: "sticky",
            bottom: 0,
          }}
        >
          {" "}
          <SingleJobApplyButtonGroup
            job={props.serverJob}
            stickyBottom={true}
          />{" "}
        </DivStyled>
      ) : null}
    </Fragment>
  );
}

export default NewSingleJobWrapper;
